import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, Trash2, Download, FolderOpen, Image as ImageIcon, X, Film, Play } from 'lucide-react';
import { mediaApi } from '../../utils/api';

const CATEGORY_LABELS = {
  news: 'समाचार',
  reels: 'रिल्स',
  ads: 'विज्ञापन',
  authors: 'लेखकहरू',
  footer: 'फुटर',
  logos: 'लोगोहरू',
  team: 'टिम फोटाहरू',
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function downloadZip(category) {
  const url = mediaApi.downloadZipUrl(category);
  fetch(url, {
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
  })
    .then((res) => {
      if (!res.ok) throw new Error('Download failed');
      return res.blob();
    })
    .then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `kalonews-${category || 'all'}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('ZIP डाउनलोड सुरु भयो।');
    })
    .catch(() => toast.error('डाउनलोड असफल भयो।'));
}

// Click gareko photo/video thulo modal ma dekhaune
function MediaModal({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <button type="button" onClick={onClose} aria-label="बन्द गर्नुहोस्" className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 text-white hover:bg-white hover:text-black transition-colors">
        <X size={22} />
      </button>
      <div className="max-w-[92vw] max-h-[86vh] flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center max-h-[78vh]">
          {item.type === 'video' ? (
            <video src={item.url} controls autoPlay className="max-w-full max-h-[78vh] rounded-xl shadow-2xl" />
          ) : (
            <img src={item.url} alt={item.name} className="max-w-full max-h-[78vh] object-contain rounded-xl shadow-2xl" />
          )}
        </div>
        <p className="text-center text-white/85 text-[12.5px] font-semibold truncate">{item.name}</p>
      </div>
    </div>
  );
}

function AdminAllPhotos() {
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null); // filename being deleted
  const [viewing, setViewing] = useState(null); // media open in modal

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const res = await mediaApi.getAll();
      setCategories(res.data?.categories || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast.error('फोटोहरू लोड गर्न असफल भयो।');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleDelete = async (category, name) => {
    if (!window.confirm(`"${name}" मेट्न चाहनुहुन्छ? यो कार्य फिर्ता हुन सक्दैन।`)) return;
    setDeleting(name);
    try {
      await mediaApi.deletePhoto(category, name);
      toast.success('मेटियो।');
      setViewing((v) => (v && v.name === name ? null : v));
      await fetchPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error(error.response?.data?.error || 'मेट्न असफल भयो।');
    } finally {
      setDeleting(null);
    }
  };

  const active = activeCat === 'all' ? categories : categories.filter((c) => c.category === activeCat);
  const totalCount = categories.reduce((s, c) => s + c.count, 0);

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-2xl">अपलोड गरिएका मिडिया</h2>
          <p className="text-[12.5px] text-muted mt-0.5">कुल {totalCount} फाइल • {categories.length} फोल्डर</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={fetchPhotos} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-line text-[13px] font-semibold text-ink-soft hover:bg-paper-soft transition-colors">
            <RefreshCw size={14} /> रिफ्रेस
          </button>
          <button onClick={() => downloadZip(null)} disabled={totalCount === 0} className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13px] px-5 py-2.5 rounded-full hover:bg-crimson-dark transition-colors disabled:opacity-60">
            <Download size={15} /> सबै ZIP डाउनलोड
          </button>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <button
          onClick={() => setActiveCat('all')}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold border transition-colors ${
            activeCat === 'all' ? 'bg-crimson text-white border-crimson' : 'bg-white text-ink-soft border-line hover:border-crimson'
          }`}
        >
          <FolderOpen size={13} /> सबै ({totalCount})
        </button>
        {categories.map((c) => (
          <button
            key={c.category}
            onClick={() => setActiveCat(c.category)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold border transition-colors ${
              activeCat === c.category ? 'bg-crimson text-white border-crimson' : 'bg-white text-ink-soft border-line hover:border-crimson'
            }`}
          >
            <FolderOpen size={13} /> {CATEGORY_LABELS[c.category] || c.category} ({c.count})
          </button>
        ))}
      </div>

      {active.length === 0 || totalCount === 0 ? (
        <div className="text-center py-16">
          <ImageIcon size={48} className="mx-auto text-muted mb-3" />
          <h3 className="font-display text-xl font-bold text-ink mb-1">कुनै फोटो छैन</h3>
          <p className="text-muted text-[13px]">अपलोड फोल्डर खाली छ।</p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {active.map((cat) => (
            <section key={cat.category}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <h3 className="font-display text-lg font-bold">
                  {CATEGORY_LABELS[cat.category] || cat.category}
                  <span className="ml-2 text-[12px] font-semibold text-muted">{cat.count} फाइल</span>
                </h3>
                <button onClick={() => downloadZip(cat.category)} disabled={cat.count === 0} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-line text-[12px] font-semibold text-ink-soft hover:border-crimson hover:text-crimson transition-colors disabled:opacity-50">
                  <Download size={13} /> ZIP डाउनलोड
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {cat.photos.map((p) => (
                  <div key={p.url} className="bg-white border border-line rounded-xl overflow-hidden group relative">
                    <button
                      type="button"
                      aria-label={`${p.name} हेर्नुहोस्`}
                      onClick={() => setViewing({ ...p })}
                      className={`block w-full aspect-square bg-paper-soft overflow-hidden ${p.type === 'video' ? 'relative' : ''}`}
                    >
                      {p.type === 'video' ? (
                        <>
                          <video src={`${p.url}#t=0.1`} preload="metadata" muted className="w-full h-full object-cover" />
                          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center group-hover:bg-crimson transition-colors">
                              <Play size={18} fill="currentColor" />
                            </span>
                          </span>
                        </>
                      ) : (
                        <img src={p.url} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      )}
                    </button>
                    <div className="p-2">
                      <p className="text-[11px] font-semibold text-ink truncate flex items-center gap-1" title={p.name}>
                        {p.type === 'video' && <Film size={11} className="flex-shrink-0 text-muted" />} {p.name}
                      </p>
                      <p className="text-[10px] text-muted">{formatBytes(p.size)}</p>
                    </div>
                    <button
                      type="button"
                      aria-label={`${p.name} मेट्नुहोस्`}
                      title="मेट्नुहोस्"
                      disabled={deleting === p.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(cat.category, p.name);
                      }}
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-white/90 border border-line text-ink-soft hover:text-white hover:bg-crimson hover:border-crimson shadow-sm transition-colors disabled:opacity-50"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {viewing && (
        <MediaModal
          item={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

export default AdminAllPhotos;